/**
 * editor/draft.ts
 *
 * Pure model for the puzzle editor: drafts, painting and filling cells,
 * resizing (from any side) and trimming, turning a draft into a playable
 * puzzle, and "reveal cells until the puzzle has one solution".
 */
import { cluesFromSolution, type PuzzleDefinition } from '@nonogram/shared';
import type { PuzzleValidation, PuzzleValidatorPort } from '../ports/puzzle-validator';

export const EDITOR_MIN_SIZE = 2;
export const EDITOR_MAX_SIZE = 200;
export const EDITOR_MAX_COLORS = 16;
export const DEFAULT_EDITOR_COLORS = ['#ffffff', '#222222'];

/** A puzzle being made on this device. Cells use the board encoding (0 empty, 1+ color). */
export interface PuzzleDraft {
  id: string;
  name: string;
  rows: number;
  columns: number;
  /** colors[0] is the background; 1+ are drawing colors. */
  colors: string[];
  cells: number[];
  /** Cells revealed (and locked) when the puzzle starts. */
  givens: number[];
  createdAt: number;
  updatedAt: number;
  /** The last share of this draft: its public id and the content it had. */
  shared?: { puzzleId: string; fingerprint: string; sharedAt: number };
  /**
   * Set when the draft edits a catalog puzzle (an admin's Edit in editor):
   * which one, and the draft's fingerprint when it was last saved there.
   */
  catalog?: { puzzleId: string; savedFingerprint: string };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return EDITOR_MIN_SIZE;
  return Math.min(EDITOR_MAX_SIZE, Math.max(EDITOR_MIN_SIZE, Math.round(value)));
}

export function createDraft(options: { id: string; name?: string; rows: number; columns: number; now: number }): PuzzleDraft {
  const rows = clampSize(options.rows);
  const columns = clampSize(options.columns);
  return {
    id: options.id,
    name: options.name?.trim() || 'Untitled puzzle',
    rows,
    columns,
    colors: [...DEFAULT_EDITOR_COLORS],
    cells: new Array(rows * columns).fill(0),
    givens: [],
    createdAt: options.now,
    updatedAt: options.now,
  };
}

/** Why the editor can't take a catalog puzzle, or null when it can. */
export function catalogEditBlocker(puzzle: Pick<PuzzleDefinition, 'rows' | 'columns' | 'colors' | 'solution' | 'parts' | 'partOf'>): string | null {
  if (puzzle.parts) return 'It is split into parts; remove its parts to redraw it.';
  if (puzzle.partOf) return 'It is a part of a multi-part puzzle; redraw the whole puzzle instead.';
  if (puzzle.rows < EDITOR_MIN_SIZE || puzzle.columns < EDITOR_MIN_SIZE || puzzle.rows > EDITOR_MAX_SIZE || puzzle.columns > EDITOR_MAX_SIZE) {
    return `The editor takes ${EDITOR_MIN_SIZE} to ${EDITOR_MAX_SIZE} cells per side; this is ${puzzle.rows}×${puzzle.columns}.`;
  }
  if (puzzle.colors.length > EDITOR_MAX_COLORS) return `The editor takes up to ${EDITOR_MAX_COLORS - 1} colors; this has ${puzzle.colors.length - 1}.`;
  if (puzzle.solution.length !== puzzle.rows * puzzle.columns) return "Its picture doesn't match its size.";
  return null;
}

/** A draft that edits a catalog puzzle (see `catalogEditBlocker` for which it can take). */
export function draftFromCatalogPuzzle(puzzle: PuzzleDefinition, options: { id: string; now: number }): PuzzleDraft {
  const draft: PuzzleDraft = {
    id: options.id,
    name: puzzle.name,
    rows: puzzle.rows,
    columns: puzzle.columns,
    colors: [...puzzle.colors],
    cells: puzzle.solution.map((value) => (value > 0 && value < puzzle.colors.length ? value : 0)),
    givens: [...new Set(puzzle.givens?.indices ?? [])].filter((index) => index >= 0 && index < puzzle.solution.length).sort((a, b) => a - b),
    createdAt: options.now,
    updatedAt: options.now,
  };
  return { ...draft, catalog: { puzzleId: puzzle.id, savedFingerprint: draftFingerprint(draft) } };
}

/** Whether a catalog draft has changes not yet saved to the catalog. */
export function hasUnsavedCatalogChanges(draft: PuzzleDraft): boolean {
  return Boolean(draft.catalog && draft.catalog.savedFingerprint !== draftFingerprint(draft));
}

/**
 * The catalog puzzle with the draft's drawing and name, keeping the rest
 * (author, tags, description, type, source). Clues come from the drawing.
 */
export function catalogPuzzleFromDraft(current: PuzzleDefinition, draft: PuzzleDraft): PuzzleDefinition {
  const drawn = draftToPuzzle(draft, current.id);
  const { givens: _previous, ...rest } = current;
  return {
    ...rest,
    name: draft.name,
    rows: drawn.rows,
    columns: drawn.columns,
    colors: drawn.colors,
    solution: drawn.solution,
    rowClues: drawn.rowClues,
    columnClues: drawn.columnClues,
    ...(drawn.givens ? { givens: drawn.givens } : {}),
  };
}

/** Sets cells to `value`. Returns the same array when nothing changes. */
export function paintCells(cells: number[], indices: number[], value: number): number[] {
  if (!indices.some((index) => cells[index] !== value)) return cells;
  const next = cells.slice();
  for (const index of indices) if (index >= 0 && index < next.length) next[index] = value;
  return next;
}

/**
 * Applies a finished stroke to the draft as it is now (something else, like
 * Reveal, may have changed it during the drag). A stroke drawn on a
 * different size no longer fits and is dropped.
 */
export function applyStroke(
  draft: PuzzleDraft,
  stroke: { rows: number; columns: number; indices: number[]; values: number[]; givensAdded: number[]; givensRemoved: number[] },
): PuzzleDraft {
  if (draft.rows !== stroke.rows || draft.columns !== stroke.columns) return draft;
  const cells = paintIndividually(draft.cells, stroke.indices, stroke.values);
  const before = new Set(draft.givens);
  const givensChanged = stroke.givensAdded.some((index) => !before.has(index))
    || stroke.givensRemoved.some((index) => before.has(index));
  if (cells === draft.cells && !givensChanged) return draft;
  if (!givensChanged) return { ...draft, cells };
  const givens = new Set(before);
  for (const index of stroke.givensAdded) givens.add(index);
  for (const index of stroke.givensRemoved) givens.delete(index);
  return { ...draft, cells, givens: [...givens].sort((a, b) => a - b) };
}

function paintIndividually(cells: number[], indices: number[], values: number[]): number[] {
  if (!indices.some((index, i) => cells[index] !== values[i])) return cells;
  const next = cells.slice();
  indices.forEach((index, i) => {
    if (index >= 0 && index < next.length) next[index] = values[i];
  });
  return next;
}

/** Cells connected (4-way) to `start` with the same value. */
export function floodFillIndices(cells: number[], rows: number, columns: number, start: number): number[] {
  if (start < 0 || start >= rows * columns) return [];
  const target = cells[start];
  const seen = new Uint8Array(rows * columns);
  const stack = [start];
  const result: number[] = [];
  seen[start] = 1;
  while (stack.length > 0) {
    const index = stack.pop()!;
    result.push(index);
    const row = Math.floor(index / columns);
    const column = index % columns;
    const neighbors = [
      row > 0 ? index - columns : -1,
      row < rows - 1 ? index + columns : -1,
      column > 0 ? index - 1 : -1,
      column < columns - 1 ? index + 1 : -1,
    ];
    for (const next of neighbors) {
      if (next >= 0 && !seen[next] && cells[next] === target) {
        seen[next] = 1;
        stack.push(next);
      }
    }
  }
  return result;
}

/** Rows or columns to add (positive) or take away (negative) on each side. */
export interface EdgeChange {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const NO_EDGE_CHANGE: EdgeChange = { top: 0, right: 0, bottom: 0, left: 0 };

type GridCells = Pick<PuzzleDraft, 'rows' | 'columns' | 'cells' | 'givens'>;

/**
 * Keeps one axis's change within size limits: a size that would get too
 * small takes back removals, and one too big drops additions, from the end
 * (right or bottom) side first.
 */
function clampAxis(size: number, start: number, end: number): [number, number] {
  let before = Math.round(start);
  let after = Math.round(end);
  const short = EDITOR_MIN_SIZE - (size + before + after);
  if (short > 0) {
    const back = Math.min(short, Math.max(0, -after));
    after += back;
    before += Math.min(short - back, Math.max(0, -before));
  }
  const over = size + before + after - EDITOR_MAX_SIZE;
  if (over > 0) {
    const cut = Math.min(over, Math.max(0, after));
    after -= cut;
    before -= Math.min(over - cut, Math.max(0, before));
  }
  return [before, after];
}

/** The change, limited so the size stays within EDITOR_MIN_SIZE to EDITOR_MAX_SIZE per side. */
export function clampEdgeChange(size: { rows: number; columns: number }, change: EdgeChange): EdgeChange {
  const [top, bottom] = clampAxis(size.rows, change.top, change.bottom);
  const [left, right] = clampAxis(size.columns, change.left, change.right);
  return { top, right, bottom, left };
}

export function isNoEdgeChange(change: EdgeChange): boolean {
  return change.top === 0 && change.right === 0 && change.bottom === 0 && change.left === 0;
}

/**
 * Adds or takes away rows and columns on any side. The drawing (and its
 * givens) stays where it is; cells taken away are dropped. Returns the same
 * object when nothing changes.
 */
export function resizeEdges<T extends GridCells>(grid: T, change: EdgeChange): T {
  const clamped = clampEdgeChange(grid, change);
  if (isNoEdgeChange(clamped)) return grid;
  const { top, right, bottom, left } = clamped;
  const rows = grid.rows + top + bottom;
  const columns = grid.columns + left + right;
  // The new position of an old cell, or -1 once it's cut off.
  const moved = (index: number) => {
    const row = Math.floor(index / grid.columns) + top;
    const column = (index % grid.columns) + left;
    return row >= 0 && row < rows && column >= 0 && column < columns ? row * columns + column : -1;
  };
  const cells = new Array(rows * columns).fill(0);
  for (let row = Math.max(0, top); row < Math.min(rows, grid.rows + top); row++) {
    for (let column = Math.max(0, left); column < Math.min(columns, grid.columns + left); column++) {
      cells[row * columns + column] = grid.cells[(row - top) * grid.columns + (column - left)];
    }
  }
  const givens = grid.givens.map(moved).filter((index) => index >= 0).sort((a, b) => a - b);
  return { ...grid, rows, columns, cells, givens };
}

/** A new size, keeping the top-left of the drawing (and givens inside it). */
export function resizeDraft(draft: PuzzleDraft, rows: number, columns: number): PuzzleDraft {
  return resizeEdges(draft, { top: 0, left: 0, bottom: clampSize(rows) - draft.rows, right: clampSize(columns) - draft.columns });
}

/**
 * The change that crops the grid to its drawing (filled and revealed cells),
 * kept at least 2 by 2; null when there's nothing drawn or nothing to crop.
 */
export function trimChange(grid: GridCells): EdgeChange | null {
  const givens = new Set(grid.givens);
  let top = grid.rows;
  let bottom = -1;
  let left = grid.columns;
  let right = -1;
  for (let index = 0; index < grid.cells.length; index++) {
    if (grid.cells[index] === 0 && !givens.has(index)) continue;
    const row = Math.floor(index / grid.columns);
    const column = index % grid.columns;
    top = Math.min(top, row);
    bottom = Math.max(bottom, row);
    left = Math.min(left, column);
    right = Math.max(right, column);
  }
  if (bottom < 0) return null;
  const change = clampEdgeChange(grid, { top: -top, right: right - grid.columns + 1, bottom: bottom - grid.rows + 1, left: -left });
  return isNoEdgeChange(change) ? null : change;
}

/** How many cells each palette color paints (index 0, the background, counts the empty ones). */
export function colorUsage(draft: Pick<PuzzleDraft, 'colors' | 'cells'>): number[] {
  const counts = new Array<number>(draft.colors.length).fill(0);
  for (const value of draft.cells) {
    if (value >= 0 && value < counts.length) counts[value]!++;
  }
  return counts;
}

/** A drawing color can go while another is left (the background always stays). */
export function canRemoveColor(draft: Pick<PuzzleDraft, 'colors'>): boolean {
  return draft.colors.length > 2;
}

/**
 * Removes a drawing color: its cells become empty and higher colors shift
 * down one index. The background (0) and the last drawing color stay.
 */
export function removeColor(draft: PuzzleDraft, colorIndex: number): PuzzleDraft {
  if (colorIndex < 1 || colorIndex >= draft.colors.length || !canRemoveColor(draft)) return draft;
  return {
    ...draft,
    colors: draft.colors.filter((_, index) => index !== colorIndex),
    cells: draft.cells.map((value) => (value === colorIndex ? 0 : value > colorIndex ? value - 1 : value)),
  };
}

/** A short hash of everything that makes up the puzzle (to tell if it changed since sharing). */
export function draftFingerprint(draft: Pick<PuzzleDraft, 'rows' | 'columns' | 'colors' | 'cells' | 'givens' | 'name'> & Partial<PuzzleDraft>): string {
  // Drafts are never changed in place, so a draft object's fingerprint can be kept (the page asks on every render).
  let fingerprint = fingerprints.get(draft);
  if (fingerprint === undefined) {
    fingerprint = computeFingerprint(draft);
    fingerprints.set(draft, fingerprint);
  }
  return fingerprint;
}

const fingerprints = new WeakMap<object, string>();

function computeFingerprint(draft: Pick<PuzzleDraft, 'rows' | 'columns' | 'colors' | 'cells' | 'givens' | 'name'>): string {
  const text = JSON.stringify([draft.name, draft.rows, draft.columns, draft.colors, draft.cells, [...draft.givens].sort((a, b) => a - b)]);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export function draftIsEmpty(draft: Pick<PuzzleDraft, 'cells'>): boolean {
  return !draft.cells.some((value) => value > 0);
}

/** The draft as a playable puzzle (clues derived from the drawing). */
export function draftToPuzzle(draft: PuzzleDraft, id = `draft:${draft.id}`): PuzzleDefinition {
  const { rowClues, columnClues } = cluesFromSolution(draft.cells, draft.rows, draft.columns);
  const givens = [...new Set(draft.givens)].filter((index) => index >= 0 && index < draft.cells.length).sort((a, b) => a - b);
  return {
    id,
    name: draft.name,
    type: 'custom',
    tags: [],
    rows: draft.rows,
    columns: draft.columns,
    colors: [...draft.colors],
    solution: [...draft.cells],
    rowClues,
    columnClues,
    ...(givens.length > 0 ? { givens: { indices: givens, values: givens.map((index) => draft.cells[index]) } } : {}),
  };
}

/** The starting board for a check: givens revealed, the rest unknown (-1). */
export function givensBoard(draft: Pick<PuzzleDraft, 'cells' | 'givens'>, givens: number[] = draft.givens): number[] {
  const board = new Array(draft.cells.length).fill(-1);
  for (const index of givens) if (index >= 0 && index < board.length) board[index] = draft.cells[index];
  return board;
}

/** Checks the draft's clues (with its givens) for a single solution. */
export function validateDraft(draft: PuzzleDraft, validator: PuzzleValidatorPort, givens: number[] = draft.givens): Promise<PuzzleValidation> {
  const { rowClues, columnClues } = cluesFromSolution(draft.cells, draft.rows, draft.columns);
  return validator.validate({
    rows: draft.rows,
    columns: draft.columns,
    rowClues,
    columnClues,
    ...(givens.length > 0 ? { initialBoard: givensBoard(draft, givens) } : {}),
  });
}

export interface RevealResult {
  status: 'unique' | 'gave-up' | 'timeout' | 'unsolvable';
  givens: number[];
  /** Cells added to the givens. */
  added: number[];
}

/**
 * Reveals cells until the clues have exactly one solution. While there are
 * two solutions, reveal a cell where they differ: the drawing disagrees with
 * at least one of them there, so that solution is ruled out each step. Then
 * drop any revealed cell the uniqueness doesn't need, newest first.
 */
export async function revealUntilUnique(
  draft: PuzzleDraft,
  validator: PuzzleValidatorPort,
  options: { maxSteps?: number; maxPrune?: number } = {},
): Promise<RevealResult> {
  const maxSteps = options.maxSteps ?? 200;
  const maxPrune = options.maxPrune ?? 40;
  const start = [...new Set(draft.givens)];
  const givens = [...start];
  const given = new Set(givens);

  for (let step = 0; ; step++) {
    const check = await validateDraft(draft, validator, givens);
    if (check.status === 'unique') break;
    if (check.status !== 'multiple' || check.solutions.length < 2) {
      return { status: check.status === 'timeout' ? 'timeout' : 'unsolvable', givens: start, added: [] };
    }
    if (step >= maxSteps) return { status: 'gave-up', givens: start, added: [] };
    const [a, b] = check.solutions;
    const differing: number[] = [];
    for (let index = 0; index < a.length; index++) {
      if (a[index] !== b[index] && !given.has(index)) differing.push(index);
    }
    if (differing.length === 0) return { status: 'gave-up', givens: start, added: [] };
    // Prefer a cell the drawing fills (a colored clue is easier to read than a gap),
    // then the middle of the differing cells.
    const filled = differing.filter((index) => draft.cells[index] > 0);
    const pool = filled.length > 0 ? filled : differing;
    const pick = pool[Math.floor(pool.length / 2)];
    givens.push(pick);
    given.add(pick);
  }

  // Prune: newly added cells the answer no longer depends on.
  const added = givens.filter((index) => !start.includes(index));
  if (added.length <= maxPrune) {
    for (let i = added.length - 1; i >= 0; i--) {
      const without = givens.filter((index) => index !== added[i]);
      const check = await validateDraft(draft, validator, without);
      if (check.status === 'unique') {
        givens.splice(givens.indexOf(added[i]), 1);
      }
    }
  }
  return { status: 'unique', givens, added: givens.filter((index) => !start.includes(index)) };
}
